/* Custom Product Purchase
 * Per-section instance. Reads JSON data from <script data-cpp-data> in the same section root,
 * handles variant selection, gallery image swap, selling-plan-driven subscription widget,
 * one-time vs subscription mode toggle, dynamic pricing, qty stepper, and add-to-cart
 * (including cart-drawer rendering when the theme uses a drawer cart).
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10);
    if (isNaN(cents) || cents == null) return '';
    var fmt = format || '${{amount}}';
    function thousands(num, decimals, decSep, thouSep) {
      var n = Math.abs(num) / 100;
      var fixed = n.toFixed(decimals == null ? 2 : decimals);
      var parts = fixed.split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + thouSep);
      var dec = parts[1] ? decSep + parts[1] : '';
      return (num < 0 ? '-' : '') + intPart + dec;
    }
    var value = '';
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount':
        value = thousands(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals':
        value = thousands(cents, 0, '.', ',');
        break;
      case 'amount_with_comma_separator':
        value = thousands(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = thousands(cents, 0, ',', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = thousands(cents, 2, '.', "'");
        break;
      default:
        value = thousands(cents, 2, '.', ',');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function applyPlanAdjustment(price, adjustment) {
    if (!adjustment) return price;
    var v = adjustment.value;
    if (adjustment.value_type === 'percentage') {
      return Math.round((price * (100 - v)) / 100);
    }
    if (adjustment.value_type === 'fixed_amount') {
      return Math.max(0, price - v);
    }
    if (adjustment.value_type === 'price') {
      return v;
    }
    return price;
  }

  function planPriceFor(variant, plan) {
    if (!plan || !plan.price_adjustments || !plan.price_adjustments.length) return variant.price;
    return applyPlanAdjustment(variant.price, plan.price_adjustments[0]);
  }

  function CPPInstance(root) {
    this.root = root;
    var dataNode = root.querySelector('[data-cpp-data]');
    if (!dataNode) return;
    try {
      this.data = JSON.parse(dataNode.textContent);
    } catch (e) {
      console.error('[custom-product-purchase] Bad JSON', e);
      return;
    }
    this.moneyFormat = this.data.money_format || '${{amount}}';
    this.variants = this.data.variants || {};
    this.plans = this.data.selling_plans || {};
    this.qty = 1;
    this.mode = 'sub'; // 'sub' or 'onetime'

    var variantIds = Object.keys(this.variants);
    var first = this.data.default_variant_id && this.variants[this.data.default_variant_id]
      ? this.data.default_variant_id
      : variantIds[0];
    this.currentVariantId = first;

    this.cacheRefs();
    this.bindEvents();
    this.applyVariant(this.currentVariantId, true);
  }

  CPPInstance.prototype.cacheRefs = function () {
    var r = this.root;
    this.refs = {
      mainImg: r.querySelector('[data-cpp-main]'),
      thumbs: Array.prototype.slice.call(r.querySelectorAll('[data-cpp-thumb]')),
      thumbTrack: r.querySelector('[data-cpp-thumb-track]'),
      thumbArrows: Array.prototype.slice.call(r.querySelectorAll('[data-cpp-thumb-arrow]')),
      packs: Array.prototype.slice.call(r.querySelectorAll('[data-cpp-variant]')),
      subCard: r.querySelector('[data-cpp-mode-card="sub"]'),
      onetimeCard: r.querySelector('[data-cpp-mode-card="onetime"]'),
      subToggle: r.querySelector('[data-cpp-mode-toggle="sub"]'),
      onetimeToggle: r.querySelector('[data-cpp-mode-toggle="onetime"]'),
      freqOptions: r.querySelector('[data-cpp-freq-options]'),
      subPriceCurrent: r.querySelector('[data-cpp-sub-price-current]'),
      subPriceCompare: r.querySelector('[data-cpp-sub-price-compare]'),
      onetimePriceCurrent: r.querySelector('[data-cpp-onetime-price-current]'),
      onetimePriceCompare: r.querySelector('[data-cpp-onetime-price-compare]'),
      subToggleAmount: r.querySelector('[data-cpp-sub-toggle-amount]'),
      onetimeToggleAmount: r.querySelector('[data-cpp-onetime-toggle-amount]'),
      qtyInput: r.querySelectorAll('[data-cpp-qty-input]'),
      qtyStep: r.querySelectorAll('[data-cpp-qty]'),
      addBtns: r.querySelectorAll('[data-cpp-add]'),
      hiddenVariantInput: r.querySelector('[data-cpp-variant-input]'),
      hiddenPlanInput: r.querySelector('[data-cpp-plan-input]')
    };
  };

  CPPInstance.prototype.bindEvents = function () {
    var self = this;

    this.refs.packs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.applyVariant(btn.getAttribute('data-cpp-variant'));
      });
    });

    if (this.refs.subToggle) {
      this.refs.subToggle.addEventListener('click', function () { self.setMode('sub'); });
    }
    if (this.refs.onetimeToggle) {
      this.refs.onetimeToggle.addEventListener('click', function () { self.setMode('onetime'); });
    }

    this.refs.qtyStep.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.getAttribute('data-cpp-qty'), 10) || 0;
        self.qty = Math.max(1, self.qty + step);
        self.syncQty();
      });
    });
    this.refs.qtyInput.forEach(function (inp) {
      inp.addEventListener('change', function () {
        var v = parseInt(inp.value, 10);
        self.qty = Math.max(1, isNaN(v) ? 1 : v);
        self.syncQty();
      });
    });

    this.refs.addBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.addToCart(btn);
      });
    });

    this.refs.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var img = thumb.getAttribute('data-image');
        var alt = thumb.getAttribute('data-alt') || '';
        if (img) self.swapMainImage(img, alt);
        self.setActive(self.refs.thumbs, thumb);
      });
    });

    this.refs.thumbArrows.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var thumbs = self.refs.thumbs;
        if (!thumbs.length) return;
        var dir = btn.getAttribute('data-cpp-thumb-arrow') === 'next' ? 1 : -1;

        // Find the currently active thumb.
        var activeIdx = -1;
        for (var i = 0; i < thumbs.length; i++) {
          if (thumbs[i].classList.contains('is-active')) {
            activeIdx = i;
            break;
          }
        }
        if (activeIdx === -1) activeIdx = 0;

        // Advance to next/prev with wrap-around.
        var nextIdx = activeIdx + dir;
        if (nextIdx < 0) nextIdx = thumbs.length - 1;
        if (nextIdx >= thumbs.length) nextIdx = 0;

        var nextThumb = thumbs[nextIdx];
        if (!nextThumb) return;

        // Activating the thumb swaps the main image and updates active state.
        var img = nextThumb.getAttribute('data-image');
        var alt = nextThumb.getAttribute('data-alt') || '';
        if (img) self.swapMainImage(img, alt);
        self.setActive(thumbs, nextThumb);

        // If a thumb is also tied to a variant, switch the variant too.
        var variantId = nextThumb.getAttribute('data-variant-id');
        if (variantId && self.variants[variantId]) {
          self.applyVariant(variantId);
        }

        // Make sure the new active thumb is visible in the scrollable track.
        var track = self.refs.thumbTrack;
       if (track) {
       track.scrollLeft = nextThumb.offsetLeft - track.offsetLeft;
       }
      });
    });
  };

  CPPInstance.prototype.setActive = function (group, current) {
    group.forEach(function (el) { el.classList.remove('is-active'); });
    if (current) current.classList.add('is-active');
  };

  CPPInstance.prototype.applyVariant = function (variantId, isInitial) {
    var variant = this.variants[variantId];
    if (!variant) return;
    this.currentVariantId = variantId;

    var pack = this.refs.packs.find ? this.refs.packs.find(function (p) {
      return p.getAttribute('data-cpp-variant') === String(variantId);
    }) : null;
    if (!pack) {
      for (var i = 0; i < this.refs.packs.length; i++) {
        if (this.refs.packs[i].getAttribute('data-cpp-variant') === String(variantId)) {
          pack = this.refs.packs[i];
          break;
        }
      }
    }
    this.setActive(this.refs.packs, pack);

    if (variant.image && variant.image.src) {
      this.swapMainImage(variant.image.src, variant.image.alt || '');
      var imageId = variant.image.id != null ? String(variant.image.id) : null;
      var match = null;
      if (imageId) {
        for (var j = 0; j < this.refs.thumbs.length; j++) {
          var t = this.refs.thumbs[j];
          if (t.getAttribute('data-image-id') === imageId) {
            match = t;
            break;
          }
        }
      }
      if (match) {
      this.setActive(this.refs.thumbs, match);

      // Only scroll thumbnail track after user interaction, not on page load
      if (!isInitial && this.refs.thumbTrack) {
        this.refs.thumbTrack.scrollLeft = match.offsetLeft - this.refs.thumbTrack.offsetLeft;
      }
    }
    }

    var planIds = variant.selling_plan_ids || [];
    var hasPlans = planIds.length > 0;

    if (hasPlans) {
      this.renderFrequencies(planIds);
      if (!this.currentPlanId || planIds.indexOf(this.currentPlanId) === -1) {
        this.currentPlanId = planIds[0];
      }
    } else {
      this.currentPlanId = null;
      if (this.refs.freqOptions) this.refs.freqOptions.innerHTML = '';
    }

    if (this.refs.subCard) this.refs.subCard.style.display = hasPlans ? '' : 'none';
    if (this.refs.subToggle) this.refs.subToggle.style.display = hasPlans ? '' : 'none';
    if (this.refs.onetimeToggle) this.refs.onetimeToggle.style.display = hasPlans ? '' : 'none';

    if (!hasPlans) {
      this.mode = 'onetime';
    } else if (isInitial) {
      this.mode = 'sub';
    }

    this.applyMode();
    this.updatePricing();
    this.syncHiddenInputs();

    if (variant.available === false) {
      this.refs.addBtns.forEach(function (btn) {
        btn.setAttribute('disabled', 'disabled');
        btn.dataset.originalText = btn.dataset.originalText || btn.textContent;
        btn.textContent = 'sold out';
      });
    } else {
      this.refs.addBtns.forEach(function (btn) {
        btn.removeAttribute('disabled');
        if (btn.dataset.originalText) btn.textContent = btn.dataset.originalText;
      });
    }
  };

  CPPInstance.prototype.renderFrequencies = function (planIds) {
    var self = this;
    var box = this.refs.freqOptions;
    if (!box) return;
    box.innerHTML = '';
    planIds.forEach(function (id, i) {
      var plan = self.plans[id];
      if (!plan) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cpp__freq';
      if ((self.currentPlanId && self.currentPlanId === id) || (!self.currentPlanId && i === 0)) {
        btn.classList.add('is-active');
        self.currentPlanId = id;
      }
      btn.textContent = plan.name;
      btn.setAttribute('data-cpp-plan', id);
      btn.addEventListener('click', function () {
        self.currentPlanId = id;
        var all = box.querySelectorAll('.cpp__freq');
        all.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        self.updatePricing();
        self.syncHiddenInputs();
      });
      box.appendChild(btn);
    });
  };

  CPPInstance.prototype.setMode = function (mode) {
    this.mode = mode;
    this.applyMode();
    this.updatePricing();
    this.syncHiddenInputs();
  };

  CPPInstance.prototype.applyMode = function () {
    var sub = this.mode === 'sub';
    if (this.refs.subCard) this.refs.subCard.hidden = !sub;
    if (this.refs.onetimeCard) this.refs.onetimeCard.hidden = sub;
    if (this.refs.subToggle) this.refs.subToggle.hidden = sub;
    if (this.refs.onetimeToggle) this.refs.onetimeToggle.hidden = !sub;
  };

  CPPInstance.prototype.updatePricing = function () {
    var variant = this.variants[this.currentVariantId];
    if (!variant) return;
    var basePrice = variant.price;
    var compareAt = variant.compare_at_price;

    var plan = this.currentPlanId ? this.plans[this.currentPlanId] : null;
    var subPrice = plan ? planPriceFor(variant, plan) : basePrice;

    if (this.refs.subPriceCurrent) this.refs.subPriceCurrent.textContent = formatMoney(subPrice, this.moneyFormat);
    if (this.refs.subPriceCompare) {
      var subCompare = compareAt && compareAt > subPrice ? compareAt : (subPrice < basePrice ? basePrice : null);
      if (subCompare) {
        this.refs.subPriceCompare.textContent = formatMoney(subCompare, this.moneyFormat);
        this.refs.subPriceCompare.style.display = '';
      } else {
        this.refs.subPriceCompare.textContent = '';
        this.refs.subPriceCompare.style.display = 'none';
      }
    }

    if (this.refs.onetimePriceCurrent) this.refs.onetimePriceCurrent.textContent = formatMoney(basePrice, this.moneyFormat);
    if (this.refs.onetimePriceCompare) {
      if (compareAt && compareAt > basePrice) {
        this.refs.onetimePriceCompare.textContent = formatMoney(compareAt, this.moneyFormat);
        this.refs.onetimePriceCompare.style.display = '';
      } else {
        this.refs.onetimePriceCompare.textContent = '';
        this.refs.onetimePriceCompare.style.display = 'none';
      }
    }

    if (this.refs.subToggleAmount) this.refs.subToggleAmount.textContent = formatMoney(subPrice, this.moneyFormat);
    if (this.refs.onetimeToggleAmount) this.refs.onetimeToggleAmount.textContent = formatMoney(basePrice, this.moneyFormat);
  };

  CPPInstance.prototype.syncHiddenInputs = function () {
    if (this.refs.hiddenVariantInput) this.refs.hiddenVariantInput.value = this.currentVariantId;
    if (this.refs.hiddenPlanInput) {
      this.refs.hiddenPlanInput.value = this.mode === 'sub' && this.currentPlanId ? this.currentPlanId : '';
    }
  };

  CPPInstance.prototype.syncQty = function () {
    var self = this;
    this.refs.qtyInput.forEach(function (inp) { inp.value = self.qty; });
  };

  CPPInstance.prototype.swapMainImage = function (src, alt) {
    var node = this.refs.mainImg;
    if (!node || !src) return;
    if (node.tagName === 'IMG') {
      // Shopify's image_tag filter outputs both `src` AND `srcset`. The
      // browser prefers srcset, so just updating src does nothing visible —
      // we have to clear srcset/sizes (and the data:image placeholder used
      // by lazy-loading themes) before setting the new src.
      node.removeAttribute('srcset');
      node.removeAttribute('data-srcset');
      node.removeAttribute('sizes');
      node.removeAttribute('data-src');
      node.src = src;
      node.alt = alt || '';
    } else {
      var img = document.createElement('img');
      img.src = src;
      img.alt = alt || '';
      img.setAttribute('data-cpp-main', '');
      node.replaceWith(img);
      this.refs.mainImg = img;
    }
  };

  CPPInstance.prototype.addToCart = function (triggerBtn) {
    var self = this;
    var routes = window.routes || {};
    var cartAddUrl = (routes.cart_add_url || '/cart/add') + '.js';

    var payload = {
      items: [{
        id: Number(this.currentVariantId),
        quantity: this.qty
      }],
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    };

    if (this.mode === 'sub' && this.currentPlanId) {
      payload.items[0].selling_plan = Number(this.currentPlanId);
    }

    var originalText = triggerBtn.textContent;
    triggerBtn.setAttribute('aria-disabled', 'true');
    triggerBtn.classList.add('is-loading');
    triggerBtn.textContent = 'Adding...';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) { return res.json(); })
      .then(function (response) {
        if (response.status && response.status !== 200) {
          if (response.description) console.warn('[cpp]', response.description);
          triggerBtn.textContent = response.message || 'Error';
          setTimeout(function () { triggerBtn.textContent = originalText; }, 1800);
          return;
        }
        triggerBtn.textContent = 'Added';
        setTimeout(function () { triggerBtn.textContent = originalText; }, 1500);

        var cartDrawer = document.querySelector('cart-drawer');
        var cartNotification = document.querySelector('cart-notification');
        var target = cartNotification || cartDrawer;

        if (target && typeof target.renderContents === 'function' && response.sections) {
          target.renderContents(response);
        } else {
          self.refreshCartDrawer();
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-product-purchase',
            productVariantId: self.currentVariantId,
            cartData: response
          });
        }
      })
      .catch(function (err) {
        console.error('[cpp] add-to-cart failed', err);
        triggerBtn.textContent = 'Error';
        setTimeout(function () { triggerBtn.textContent = originalText; }, 1800);
      })
      .finally(function () {
        triggerBtn.removeAttribute('aria-disabled');
        triggerBtn.classList.remove('is-loading');
      });
  };

  CPPInstance.prototype.refreshCartDrawer = function () {
    var cartDrawer = document.querySelector('cart-drawer');
    var routes = window.routes || {};
    if (!cartDrawer || !routes.cart_url) return;
    fetch(routes.cart_url + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newInner = doc.getElementById('CartDrawer');
        if (newInner) {
          var existing = cartDrawer.querySelector('#CartDrawer');
          if (existing) existing.innerHTML = newInner.innerHTML;
        }
        if (typeof cartDrawer.open === 'function') cartDrawer.open();
        else cartDrawer.classList.add('active', 'animate');
      });
  };

  function init() {
    document.querySelectorAll('[data-cpp-root]').forEach(function (root) {
      if (root.__cppInited) return;
      root.__cppInited = true;
      new CPPInstance(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
